/**
 * <DeviceCheckPanel> — le test du matériel avant le live, en une carte (organism).
 *
 * L'ancien `PreCallCheckPanel`, ses trois visages (`phase`) :
 *  - `intro` : où l'on est et ce qu'on va faire, avant que rien ne démarre — « Avant d'entrer dans
 *    ton live », une ligne, les quatre appareils et ce que l'élève FAIT à chacun (« On la mesure
 *    tout seul », « Tu dis « allô » »…), puis « Vérifier mon matériel », qui demande aussi l'accès
 *    au micro et à la caméra : la fenêtre du navigateur répond à un geste dont l'élève connaît la
 *    raison ;
 *  - `journey` : le parcours. L'en-tête dit où l'on en est en mots (« 2 sur 4 », vert seulement
 *    quand tout est réglé) et ce qui suit ; la barre avance d'un segment par étape, verte pour le
 *    réglé, ambre pour ce qui a été laissé de côté. Les quatre étapes restent listées, chacune avec
 *    sa phrase d'état ; seule celle dont c'est le tour est dépliée (`current`, un
 *    `<DeviceCheckStep>`). Une étape passée se reprend (« Réessayer »). Fini, le bilan arrive dans
 *    une carte à part (« Tout est prêt », ou ce qui reste, ligne par ligne) ;
 *  - `remembered` : rien n'a bougé depuis le dernier test sur cet appareil. L'aperçu (se voir vaut
 *    mieux que se l'entendre dire), « Caméra et micro déjà vérifiés », et « Revérifier ». Rien
 *    d'autre : à qui n'a rien à faire, on n'explique pas pourquoi.
 *
 * Le bouton d'entrée n'est pas ici : il appartient à la page, et reste atteignable à tout instant.
 *
 * Composes DS pieces only — `<Card>`, `<Title>`, `<Text>`, `<IconBubble>`, `<Button>`,
 * `<TextButton>`, `<SegmentedBar>`, `<DeviceCheckRow>`, `<Divider>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Divider } from "../../atoms/Divider"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { SegmentedBar } from "../../atoms/SegmentedBar"
import { WaitingText } from "../../atoms/WaitingText"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { frenchSpacing } from "../../utils/frenchSpacing"
import { Card } from "../../molecules/Card"
import {
    DEVICE_CHECK_BUBBLE,
    DEVICE_CHECK_ICONS,
    DeviceCheckRow,
    VERDICT_TONE,
    type UIDeviceCheckLine,
    type UIDeviceCheckStepId,
} from "../../molecules/DeviceCheckRow"

export type DeviceCheckPhase = "intro" | "journey" | "remembered"

export type DeviceCheckPanelProps = {
    phase: DeviceCheckPhase

    /** `intro` : pourquoi on refait le test (le matériel a changé, le dernier test date d'un mois). */
    memoryMiss?: string
    /** `intro` : « Vérifier mon matériel ». */
    onStart?: () => void
    /** `intro` : l'accès est demandé, le bouton tourne. */
    starting?: boolean

    /** `journey` : les quatre étapes, dans l'ordre. */
    lines?: UIDeviceCheckLine[]
    /** `journey` : l'étape dont c'est le tour ; `null` quand le parcours est fini (le bilan). */
    currentIndex?: number | null
    /** `journey` : l'étape en cours, dépliée — `<DeviceCheckStep>`. */
    current?: React.ReactNode
    /** « Réessayer » une étape passée ou restée en échec. */
    onRetry?: (id: UIDeviceCheckStepId) => void

    /** `remembered` : l'aperçu de la caméra (`<CameraPreviewFrame>`). */
    preview?: React.ReactNode
    /** `remembered` : la vérification silencieuse tourne encore. */
    rememberedPending?: boolean
    /** `remembered` : « Revérifier » — le parcours complet. */
    onRecheck?: () => void
    testID?: string
}

const TEXTS = {
    title: "Avant d'entrer dans ton live",
    lead: "On vérifie que tout marche. 30 secondes, rien à remplir.",
    start: "Vérifier mon matériel",
    checking: "Vérification avant ton live",
    promise: "Juste après, tu entres dans ton live : ton tuteur t'attend.",
    count: (done: number, total: number) => `${done} sur ${total}`,
    remembered: "Caméra et micro déjà vérifiés",
    pending: "On vérifie…",
    recheck: "Revérifier",
    retry: "Réessayer",
    ready: "Tout est prêt",
    readyText: "On te voit, on t'entend, ta connexion est bonne. Bon live !",
    trouble: (count: number) => (count > 1 ? "Il reste quelques points à régler" : "Il reste un point à régler"),
    troubleText: "Tu peux entrer quand même. Tu pourras réessayer pendant le live.",
}

/** Ce que l'élève fait à chaque appareil : ce qu'on lui annonce avant de commencer. */
const INTRO_ITEMS: { id: UIDeviceCheckStepId; label: string; does: string }[] = [
    { id: "network", label: "Ta connexion", does: "On la mesure tout seul" },
    { id: "microphone", label: "Ton micro", does: "Tu dis « allô »" },
    { id: "speaker", label: "Tes haut-parleurs", does: "Tu écoutes un petit son" },
    { id: "camera", label: "Ta caméra", does: "Tu regardes ton image" },
]

const SUMMARY_BUBBLE = 40

/** Une étape réglée : mesurée au vert, sans avoir été passée. */
const isResolved = (line: UIDeviceCheckLine) => line.verdict === "ok" && line.skipped !== true

export function DeviceCheckPanel(props: DeviceCheckPanelProps) {
    const { phase, testID } = props

    if (phase === "intro") return <Intro {...props} />
    if (phase === "remembered") return <Remembered {...props} />

    const lines = props.lines ?? []
    const currentIndex = props.currentIndex ?? null
    const resolvedCount = lines.filter(isResolved).length
    const allResolved = lines.length > 0 && resolvedCount === lines.length
    // La barre : vert pour le réglé, ambre pour ce qui a été laissé derrière, le contour pour la suite.
    const segments = lines.map((line, index) => {
        const behind = currentIndex == null || index < currentIndex
        const color: SemanticColorKey = isResolved(line) ? "success" : behind ? "warning" : "border"
        return { value: 1, color }
    })
    const unresolved = lines.filter((line) => !isResolved(line))

    return (
        <View style={styles.stack} testID={testID}>
            <Card p="0" style={styles.clip}>
                <View style={styles.head}>
                    <View style={styles.headRow}>
                        <Text size="text-lg" weight="bold" color="text-strong" style={styles.flex}>
                            {TEXTS.checking}
                        </Text>
                        <Text size="text-sm" weight="bold" color={allResolved ? "success-strong" : "text-muted"}>
                            {TEXTS.count(resolvedCount, lines.length)}
                        </Text>
                    </View>
                    <Text size="text-sm" color="text-muted">
                        {frenchSpacing(TEXTS.promise)}
                    </Text>
                    <SegmentedBar segments={segments} max={lines.length} height={4} gap="1" />
                </View>
                {lines.map((line, index) => (
                    <View key={line.id}>
                        <Divider spacing="0" color="border-subtle" />
                        {index === currentIndex ? (
                            props.current
                        ) : (
                            <View style={styles.collapsed}>
                                <DeviceCheckRow
                                    line={line}
                                    muted
                                    end={
                                        line.skipped === true && props.onRetry != null ? (
                                            <TextButton
                                                label={TEXTS.retry}
                                                size="sm"
                                                onPress={() => props.onRetry?.(line.id)}
                                            />
                                        ) : undefined
                                    }
                                />
                            </View>
                        )}
                    </View>
                ))}
            </Card>

            {currentIndex == null && lines.length > 0 && (
                <Card p="4">
                    <View style={styles.summaryHead}>
                        <IconBubble
                            iconName={allResolved ? "check" : "exclamation-circle"}
                            size={SUMMARY_BUBBLE}
                            iconSize={20}
                            color={VERDICT_TONE[allResolved ? "ok" : "warn"].fill}
                            iconColor={VERDICT_TONE[allResolved ? "ok" : "warn"].ink}
                        />
                        <Text size="text-xl" weight="bold" color="text-strong" style={styles.flex}>
                            {allResolved ? TEXTS.ready : TEXTS.trouble(unresolved.length)}
                        </Text>
                    </View>
                    <Text size="text-md" color="text-muted" style={styles.summaryText}>
                        {frenchSpacing(allResolved ? TEXTS.readyText : TEXTS.troubleText)}
                    </Text>
                    {unresolved.map((line) => (
                        <View key={line.id}>
                            <Divider spacing="0" color="border-subtle" />
                            <View style={styles.troubleRow}>
                                <IconBubble
                                    iconName={DEVICE_CHECK_ICONS[line.id]}
                                    size={28}
                                    iconSize={14}
                                    color={VERDICT_TONE.warn.fill}
                                    iconColor={VERDICT_TONE.warn.ink}
                                />
                                <Text size="text-md" weight="semibold" color="text-primary" style={styles.flex}>
                                    {line.label}
                                </Text>
                                {props.onRetry != null && (
                                    <TextButton
                                        label={TEXTS.retry}
                                        size="sm"
                                        onPress={() => props.onRetry?.(line.id)}
                                    />
                                )}
                            </View>
                        </View>
                    ))}
                </Card>
            )}
        </View>
    )
}

function Intro(props: DeviceCheckPanelProps) {
    const { memoryMiss, onStart, starting = false, testID } = props

    return (
        <Card p="5" testID={testID}>
            <View style={styles.introText}>
                <Title>{TEXTS.title}</Title>
                <Text size="text-md" color="text-muted">
                    {TEXTS.lead}
                </Text>
                {memoryMiss != null && memoryMiss !== "" && (
                    <Text size="text-md" color="text-muted">
                        {frenchSpacing(memoryMiss)}
                    </Text>
                )}
            </View>
            <View style={styles.introItems}>
                {INTRO_ITEMS.map((item) => (
                    <View key={item.id} style={styles.introItem}>
                        <IconBubble
                            iconName={DEVICE_CHECK_ICONS[item.id]}
                            size={DEVICE_CHECK_BUBBLE}
                            iconSize={16}
                            color={VERDICT_TONE.pending.fill}
                            iconColor={VERDICT_TONE.pending.ink}
                        />
                        <View style={styles.flex}>
                            <Text size="text-md" weight="bold" color="text-strong">
                                {item.label}
                            </Text>
                            <Text size="text-sm" color="text-muted">
                                {frenchSpacing(item.does)}
                            </Text>
                        </View>
                    </View>
                ))}
            </View>
            <Button label={TEXTS.start} onPress={onStart} loading={starting} fullWidth />
        </Card>
    )
}

function Remembered(props: DeviceCheckPanelProps) {
    const { preview, rememberedPending = false, onRecheck, testID } = props

    return (
        <Card p="4" testID={testID}>
            <View style={styles.remembered}>
                {preview}
                {rememberedPending ? (
                    <WaitingText size="text-md" color="text-muted">
                        {TEXTS.pending}
                    </WaitingText>
                ) : (
                    <>
                        <View style={styles.summaryHead}>
                            <IconBubble
                                iconName="check"
                                size={DEVICE_CHECK_BUBBLE}
                                iconSize={16}
                                color={VERDICT_TONE.ok.fill}
                                iconColor={VERDICT_TONE.ok.ink}
                            />
                            <Text size="text-xl" weight="bold" color="text-strong" style={styles.flex}>
                                {TEXTS.remembered}
                            </Text>
                        </View>
                        {onRecheck != null && (
                            <View style={styles.start}>
                                <TextButton label={TEXTS.recheck} onPress={onRecheck} />
                            </View>
                        )}
                    </>
                )}
            </View>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    stack: {
        gap: theme.spacing["3"],
    },
    clip: {
        overflow: "hidden",
    },
    flex: { flex: 1 },
    head: {
        gap: theme.spacing["2"],
        padding: theme.spacing["4"],
    },
    headRow: {
        flexDirection: "row",
        alignItems: "baseline",
        gap: theme.spacing["2"],
    },
    /** Une ligne repliée reste sur la carte : sa pastille dit seule si elle est réglée. */
    collapsed: {
        padding: theme.spacing["4"],
    },
    summaryHead: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["3"],
    },
    summaryText: {
        marginTop: theme.spacing["3"],
        marginBottom: theme.spacing["2"],
    },
    troubleRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["3"],
        paddingVertical: theme.spacing["2"],
    },
    introText: {
        gap: theme.spacing["2"],
    },
    introItems: {
        gap: theme.spacing["3"],
        marginVertical: theme.spacing["5"],
    },
    introItem: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["3"],
    },
    remembered: {
        gap: theme.spacing["4"],
    },
    start: {
        alignItems: "flex-start",
    },
}))
